import React from 'react';
import { useNode, useEditor } from '@craftjs/core';
import useClientStore from '@/store/useClientStore';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { isBlankHtml } from '@/utils/functions';
import { trackConversionFromHref } from '@/lib/track-site-event';
import { waLinkDigits, forcedSiteLanguage } from '@/lib/country';
import { AUTO_FIT_ROW, autoFitItem, normalizeColumns } from '../../utils/autoFitRow';
import {
  IconCheck,
  IconCurrencyDollar,
  IconUserCircle,
  IconCar,
  IconStar,
  IconShield,
  IconHome,
  IconClock,
  IconSettings,
  IconPhone,
  IconMail,
  IconMap,
} from '@tabler/icons-react';
import { htmlSeguro } from '../../utils/htmlSeguro';

import { AreaDePiezas, Pieza } from '../../utils/piezaLibre';
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  link?: string;
  cardBgColor: string;
  cardTextColor: string;
  descriptionColor: string;
  cardStyle: string;
  iconColor: string;
  className?: string;
}

const FeatureCard = ({
  icon,
  title,
  description,
  link,
  cardBgColor,
  cardTextColor,
  descriptionColor,
  cardStyle,
  iconColor,
  className,
}: FeatureCardProps) => {
  const { isEnabled } = useEditor((state) => ({
    isEnabled: state.options.enabled,
  }));
  const country = useClientStore((s) => s.client?.country);

  const handleClick = () => {
    if (isEnabled || !link) return;

    // Phone/WhatsApp detection
    const phoneRegex = /^(\+?[\d\s\-\(\)]+)$/;
    if (phoneRegex.test(link.trim())) {
      const clean = link.replace(/[\s\-\(\)]/g, '');
      // Con '+' ya es internacional; si no, el código del PAÍS del tenant
      // (récord `waLinkDigits` de lib/country: CL +56 y BR +55 como siempre).
      const num = clean.startsWith('+') ? clean : `+${waLinkDigits(clean, country)}`;
      const waUrl = `https://wa.me/${num}`;
      trackConversionFromHref(waUrl); // site_events: whatsapp_click (no es <a>)
      window.open(waUrl, '_blank');
      return;
    }

    if (link.startsWith('http://') || link.startsWith('https://')) {
      trackConversionFromHref(link);
      window.open(link, '_blank');
    } else if (link.startsWith('#')) {
      document.querySelector(link)?.scrollIntoView({ behavior: 'smooth' });
    } else if (link.startsWith('/')) {
      window.location.href = link;
    } else {
      const url = `https://${link}`;
      trackConversionFromHref(url);
      window.open(url, '_blank');
    }
  };

  const getCardClasses = () => {
    const base = `${className ?? ''} rounded-xl p-8 flex flex-col items-center text-center transition-all duration-300`;
    switch (cardStyle) {
      case 'elevated':
        return `${base} shadow-md hover:shadow-xl hover:-translate-y-1`;
      case 'bordered':
        return `${base} border border-gray-200 hover:border-gray-300 hover:shadow-lg hover:-translate-y-1`;
      case 'glass':
        return `${base} backdrop-blur-sm border border-white/20 hover:shadow-lg hover:-translate-y-1`;
      case 'flat':
      default:
        return `${base} hover:shadow-lg hover:-translate-y-1`;
    }
  };

  return (
    <div
      className={getCardClasses()}
      style={{ backgroundColor: cardBgColor, cursor: link && !isEnabled ? 'pointer' : undefined }}
      onClick={link && !isEnabled ? handleClick : undefined}
    >
      <div
        className='mb-5 w-16 h-16 rounded-2xl flex items-center justify-center'
        style={{ backgroundColor: `${iconColor}12` }}
      >
        {icon}
      </div>
      {!isBlankHtml(title) && (
        <h3 className='text-xl font-semibold mb-3' style={{ color: cardTextColor }}
          dangerouslySetInnerHTML={htmlSeguro(title || '')} />
      )}
      {!isBlankHtml(description) && (
        <p className='text-sm leading-relaxed' style={{ color: descriptionColor }}
          dangerouslySetInnerHTML={htmlSeguro(description || '')} />
      )}
    </div>
  );
};

interface WhyChooseUsProps {
  /** Antetítulo. Antes era un literal del JSX: sin prop no se podía editar. */
  eyebrowText?: string;
  sectionTitle?: string;
  titleAlignment?: 'left' | 'center' | 'right';
  features?: {
    icon: string;
    title: string;
    description: string;
    link?: string;
  }[];
  bgColor?: string;
  textColor?: string;
  cardBgColor?: string;
  cardTextColor?: string;
  iconColor?: string;
  descriptionColor?: string;
  cardStyle?: 'flat' | 'elevated' | 'bordered' | 'glass';
  columns?: 2 | 3 | 4;
}

export const WhyChooseUs = ({
  eyebrowText = 'Nuestras ventajas',
  sectionTitle = '¿Por qué elegirnos?',
  titleAlignment = 'center',
  features = [
    {
      icon: 'check',
      title: 'Autos inspeccionados y garantizados',
      description: 'Cada vehículo pasa por un riguroso proceso de inspección para garantizar tu seguridad y tranquilidad.',
      link: '#garantia',
    },
    {
      icon: 'dollar',
      title: 'Financiamiento a tu medida',
      description: 'Planes de financiamiento flexibles diseñados para adaptarse a tus necesidades y presupuesto.',
      link: '#financiamiento',
    },
    {
      icon: 'user',
      title: 'Atención personalizada',
      description: 'Un asesor dedicado te acompaña en cada paso del proceso de compra.',
      link: '#contacto',
    },
  ],
  bgColor = '#ffffff',
  textColor = '#111827',
  cardBgColor = '#f9fafb',
  cardTextColor = '#111827',
  iconColor,
  descriptionColor = '#6b7280',
  cardStyle = 'bordered',
  columns = 3,
}: WhyChooseUsProps) => {
  const { connectors, selected, id } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { client } = useClientStore();
  const { t, currentLanguage } = useTranslation();
  // EE.UU. (inglés obligatorio por país): el texto del builder va TAL CUAL.
  const isTranslated = forcedSiteLanguage(client?.country) !== 'en' && client?.has_language_selector && currentLanguage !== (client?.default_language || 'es');

  const effectiveSectionTitle = isTranslated ? t('home.whyUs.title') : sectionTitle;

  const finalIconColor = iconColor || '#3b82f6';

  const getIcon = (iconType: string, size = 32) => {
    const iconProps = { size, style: { color: finalIconColor } };

    switch (iconType) {
      case 'check': return <IconCheck {...iconProps} />;
      case 'dollar': return <IconCurrencyDollar {...iconProps} />;
      case 'user': return <IconUserCircle {...iconProps} />;
      case 'car': return <IconCar {...iconProps} />;
      case 'star': return <IconStar {...iconProps} />;
      case 'shield': return <IconShield {...iconProps} />;
      case 'home': return <IconHome {...iconProps} />;
      case 'clock': return <IconClock {...iconProps} />;
      case 'settings': return <IconSettings {...iconProps} />;
      case 'phone': return <IconPhone {...iconProps} />;
      case 'mail': return <IconMail {...iconProps} />;
      case 'map': return <IconMap {...iconProps} />;
      default: return <IconCheck {...iconProps} />;
    }
  };

  const numColumns = normalizeColumns(columns, 3);
  const itemClass = autoFitItem(
    numColumns <= 1
      ? [{ cols: 1 }]
      : numColumns === 2
        ? [{ cols: 1 }, { bp: 'md', cols: 2 }]
        : [{ cols: 1 }, { bp: 'md', cols: 2 }, { bp: 'lg', cols: numColumns }],
  );

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      style={{
        background: bgColor,
        color: textColor,
        padding: '80px 20px',
        position: 'relative',
        border: selected ? '2px dashed #666666' : '1px solid transparent',
        outline: selected ? '1px dashed #999999' : 'none',
        outlineOffset: selected ? '2px' : '0px',
      }}
      className='w-full'
    >
      <AreaDePiezas className='max-w-6xl mx-auto'>
        <div className='text-center mb-14'>
          <Pieza clave="eyebrow" tipo="texto" etiqueta="Antetítulo">
          <p
            className='text-sm font-semibold uppercase tracking-widest mb-3'
            style={{ color: finalIconColor }}
            dangerouslySetInnerHTML={htmlSeguro(eyebrowText || '')}
          />
          </Pieza>
          <Pieza clave="titulo" tipo="texto" etiqueta="Título">
          {!isBlankHtml(effectiveSectionTitle) && (
            <h2
              className='text-3xl md:text-4xl lg:text-5xl font-bold'
              style={{ color: textColor, textAlign: titleAlignment }}
              dangerouslySetInnerHTML={htmlSeguro(effectiveSectionTitle || '')}
            />
          )}
          </Pieza>
        </div>

        <Pieza clave="items" etiqueta="Ventajas">
        <div className={`${AUTO_FIT_ROW} gap-6 [--bg-gap:1.5rem]`}>
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              className={itemClass}
              icon={getIcon(feature.icon)}
              title={feature.title}
              description={feature.description}
              link={feature.link}
              cardBgColor={cardBgColor}
              cardTextColor={cardTextColor}
              descriptionColor={descriptionColor}
              cardStyle={cardStyle}
              iconColor={finalIconColor}
            />
          ))}
        </div>
        </Pieza>
      </AreaDePiezas>
    </div>
  );
};

WhyChooseUs.craft = {
  displayName: 'WhyChooseUs',
  props: {
    eyebrowText: 'Nuestras ventajas',
    sectionTitle: '¿Por qué elegirnos?',
    titleAlignment: 'center',
    features: [
      {
        icon: 'check',
        title: 'Autos inspeccionados y garantizados',
        description: 'Cada vehículo pasa por un riguroso proceso de inspección para garantizar tu seguridad y tranquilidad.',
        link: '#garantia',
      },
      {
        icon: 'dollar',
        title: 'Financiamiento a tu medida',
        description: 'Planes de financiamiento flexibles diseñados para adaptarse a tus necesidades y presupuesto.',
        link: '#financiamiento',
      },
      {
        icon: 'user',
        title: 'Atención personalizada',
        description: 'Un asesor dedicado te acompaña en cada paso del proceso de compra.',
        link: '#contacto',
      },
    ],
    bgColor: '#ffffff',
    textColor: '#111827',
    cardBgColor: '#f9fafb',
    cardTextColor: '#111827',
    iconColor: '#3b82f6',
    descriptionColor: '#6b7280',
    cardStyle: 'bordered',
    columns: 3,
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => true,
  },
};
